import React, { useState, useEffect } from "react";
import { Spin, Col, Row, Button, Input, Select, Modal } from "antd";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { FileText, Search, Archive, MessageSquare } from "lucide-react";
import {
  GenerationsHistoryTable,
  SuccessNotification,
  ErrorNotification,
} from "@components";
import { getAllAdminGenerationsPaginated } from "@api/user/admin";
import { archiveMultipleGenerations } from "@api/copilots/browser-extension/generation";
import { useTranslation } from "react-i18next";
import "@assets/css/History.css";

const { Option } = Select;
const { Search: SearchInput } = Input;

const GenerationHistoryList = () => {
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [generations, setGenerations] = useState([]);
  const [totalGenerations, setTotalGenerations] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    setFilteredData(generations);
  }, [generations]);

  useEffect(() => {
    handleFetchHistoryPaginated(page);
  }, []);

  // Fetch history paginated
  const handleFetchHistoryPaginated = async (page) => {
    setLoading(true);
    getAllAdminGenerationsPaginated(page, perPage)
      .then((res) => {
        setGenerations(res.data.generations);
        setPage(res.data.paginationData.page);
        setPerPage(res.data.paginationData.perPage);
        setTotalGenerations(res.data.paginationData.totalGenerations);
        setTotalPages(res.data.paginationData.totalPages);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log("Fetch all generations error", err);
      });
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPage(1);
    setPerPage(10);
    setLoading(true);

    getAllAdminGenerationsPaginated(page, perPage, value)
      .then((res) => {
        setGenerations(res.data.generations);
        setTotalGenerations(res.data.paginationData.totalGenerations);
        setTotalPages(res.data.paginationData.totalPages);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log("Fetch all generations error", err);
      });
  };

  const handleSort = (value) => {
    const sortedData = [...filteredData];

    if (value === "date_desc") {
      sortedData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (value === "date_asc") {
      sortedData.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    }

    setFilteredData(sortedData);
  };

  const handleRowClick = (id) => {
    if (expandedRow === id) {
      setExpandedRow(null);
    } else {
      setExpandedRow(id);
    }
  };

  const handleArchiveMultipleGenerations = async (ids) => {
    Modal.confirm({
      title: "Are you sure you want to archive selected generations?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        setLoading(true);
        try {
          await archiveMultipleGenerations(ids);
          SuccessNotification("Generations archived successfully");
          handleFetchHistoryPaginated(page);
        } catch (err) {
          ErrorNotification("Error archiving generations");
        } finally {
          setSelectedIds([]);
          setLoading(false);
        }
      },
    });
  };

  return (
    <div className="history-container">
      {/* Main Content */}
      <div className="history-content">
        {/* Header Section */}
        <div className="history-header">
          <div className="history-header-left">
            <div className="history-icon">
              <FileText size={20} color="#fff" />
            </div>
            <div>
              <h1 className="history-title">
                {t("generationsHistory.generationsHistory")}
              </h1>
              <p className="history-subtitle">
                View and manage your AI generations
              </p>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
          <Col xs={24} md={14}>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <Button
                onClick={() =>
                  history.push(`/a/ws/${user.workspaceId}/history/chats`)
                }
                className="history-tab-btn"
                icon={<MessageSquare size={16} />}
              >
                {t("generationsHistory.chats")}
              </Button>

              <Button
                onClick={() =>
                  history.push(
                    `/a/ws/${user.workspaceId}/history/generations`
                  )
                }
                className="history-tab-btn active"
                icon={<FileText size={16} />}
              >
                {t("generationsHistory.generations")}
              </Button>

              {selectedIds.length > 0 && (
                <Button
                  onClick={() =>
                    handleArchiveMultipleGenerations(selectedIds)
                  }
                  className="history-archive-btn"
                  icon={<Archive size={16} />}
                >
                  ({selectedIds.length}){" "}
                  {t("generationsHistory.archiveSelected")}
                </Button>
              )}
            </div>
          </Col>

          <Col xs={24} md={10}>
            <div style={{ display: "flex", gap: "10px" }}>
              <SearchInput
                placeholder="Search input text"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                className="history-search-input"
                prefix={<Search size={16} style={{ color: "#8B5CF6" }} />}
              />

              <Select
                defaultValue="date_desc"
                onChange={handleSort}
                className="history-sort-select"
                dropdownClassName="history-dropdown"
              >
                <Option value="date_desc">
                  {t("generationsHistory.dateNewToOld")}
                </Option>
                <Option value="date_asc">
                  {t("generationsHistory.dateOldToNew")}
                </Option>
              </Select>
            </div>
          </Col>
        </Row>

        {/* Table Section */}
        {loading ? (
          <div className="history-loading">
            <Spin size="large" />
            <span>Loading generations...</span>
          </div>
        ) : (
          <div className="history-table-container">
            <GenerationsHistoryTable
              page={page}
              setPage={setPage}
              perPage={perPage}
              totalGenerations={totalGenerations}
              totalPages={totalPages}
              generations={filteredData}
              handleFetchHistoryPaginated={handleFetchHistoryPaginated}
              expandedRow={expandedRow}
              handleRowClick={handleRowClick}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerationHistoryList;
